import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import type { WorkflowFilters } from '../../../types/workflow';
import { WorkflowStatusCodes } from '../../../types/workflow';
import type { BatchPriority } from '../../../types/workflow';
import { WorkflowStatusChip } from '../WorkflowStatusChip';
import { SAMPLE_TYPE_FILTER_OPTIONS, type SampleTypeFilterValue } from '../../../utils/sampleTypeUtils';
import { DateRangeFilter } from '../../shared/DateRangeFilter';

/**
 * Default workflow statuses for filtering.
 */
const DEFAULT_STATUSES = [
  { code: WorkflowStatusCodes.NEW, name: 'New' },
  { code: WorkflowStatusCodes.AWAITING_PO, name: 'Awaiting PO' },
  { code: WorkflowStatusCodes.AWAITING_IMPORT, name: 'Awaiting Import' },
  { code: WorkflowStatusCodes.AWAITING_REVIEW, name: 'Awaiting Review' },
  { code: WorkflowStatusCodes.AWAITING_SEND, name: 'Awaiting Send' },
  { code: WorkflowStatusCodes.AWAITING_INVOICE, name: 'Awaiting Invoice' },
  { code: WorkflowStatusCodes.COMPLETED, name: 'Completed' },
];

const DEFAULT_PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];

interface WorkflowToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  loading?: boolean;
  /** Current filter state */
  filters: WorkflowFilters;
  /** Called when a single filter value changes */
  onFilterChange: <K extends keyof WorkflowFilters>(
    key: K,
    value: WorkflowFilters[K]
  ) => void;
  /** Called to reset all filters */
  onReset: () => void;
  /** Number of currently active filters (drives badge count) */
  activeFilterCount: number;
  /** Workflow statuses for the status dropdown */
  statuses?: Array<{ code: string; name: string }>;
  /** When provided, filters the status dropdown to only show viewable statuses */
  viewableStatusCodes?: string[];
  /** Priority values for the priority dropdown */
  priorities?: string[];
  /** Called to export the current filtered list to Excel. Button hidden when omitted. */
  onExport?: () => void;
  /** Whether an export is currently in progress (disables the export button) */
  exporting?: boolean;
}

/**
 * WorkflowToolbar Component
 *
 * A consolidated toolbar containing search input, filter controls (status, priority,
 * overdue, completed), clear filters button, and refresh button.
 * This is a controlled component - all state is managed externally.
 *
 * Single Responsibility: ONLY renders the toolbar UI
 */
export const WorkflowToolbar = React.memo<WorkflowToolbarProps>(
  ({
    searchValue,
    onSearchChange,
    onRefresh,
    loading = false,
    filters,
    onFilterChange,
    onReset,
    activeFilterCount,
    statuses = DEFAULT_STATUSES,
    viewableStatusCodes,
    priorities = DEFAULT_PRIORITIES,
    onExport,
    exporting = false,
  }) => {
    // Filter statuses to only show viewable ones if viewableStatusCodes is provided
    const displayStatuses = viewableStatusCodes
      ? statuses.filter((s) => viewableStatusCodes.includes(s.code))
      : statuses;

    const currentStatusCode = filters.statusCode || '';

    const handleStatusChange = (value: string) => {
      onFilterChange('statusCode', value as WorkflowFilters['statusCode']);
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Search Input */}
        <TextField
          size="small"
          placeholder="Search client, farm, batch no, reference..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            minWidth: 250,
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
          }}
          aria-label="Search workflows by client, farm, batch number or reference"
        />

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={currentStatusCode}
            label="Status"
            onChange={(e) => handleStatusChange(e.target.value as string)}
            disabled={loading}
            renderValue={(selected) => {
              if (!selected) return <em>All Statuses</em>;
              const status = statuses.find((s) => s.code === selected);
              return status ? (
                <WorkflowStatusChip
                  statusCode={status.code}
                  statusName={status.name}
                  size="small"
                />
              ) : (
                selected
              );
            }}
          >
            <MenuItem value="">
              <em>All Statuses</em>
            </MenuItem>
            {displayStatuses.map((s) => (
              <MenuItem key={s.code} value={s.code}>
                <WorkflowStatusChip
                  statusCode={s.code}
                  statusName={s.name}
                  size="small"
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priority Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="priority-label">Priority</InputLabel>
          <Select
            labelId="priority-label"
            value={filters.priority}
            label="Priority"
            onChange={(e) =>
              onFilterChange('priority', e.target.value as BatchPriority | '')
            }
            disabled={loading}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {priorities.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sample Type Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="sample-type-label">Sample Type</InputLabel>
          <Select
            labelId="sample-type-label"
            value={filters.sampleType}
            label="Sample Type"
            onChange={(e) =>
              onFilterChange('sampleType', e.target.value as SampleTypeFilterValue)
            }
            disabled={loading}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {SAMPLE_TYPE_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Filter */}
        <DateRangeFilter
          preset={filters.datePreset}
          from={filters.dateFrom}
          to={filters.dateTo}
          disabled={loading}
          onChange={({ preset, from, to }) => {
            onFilterChange('datePreset', preset);
            onFilterChange('dateFrom', from);
            onFilterChange('dateTo', to);
          }}
        />

        {/* Overdue Only Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.overdueOnly}
              onChange={(e) => onFilterChange('overdueOnly', e.target.checked)}
              disabled={loading}
              size="small"
            />
          }
          label="Overdue only"
        />

        {/* Show Completed Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showCompleted}
              onChange={(e) =>
                onFilterChange('showCompleted', e.target.checked)
              }
              disabled={loading}
              size="small"
            />
          }
          label="Show completed"
        />

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Clear Filters Button */}
        <Badge
          badgeContent={activeFilterCount}
          color="primary"
          invisible={activeFilterCount === 0}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={onReset}
            disabled={loading || activeFilterCount === 0}
          >
            Clear Filters
          </Button>
        </Badge>

        {/* Export Button */}
        {onExport && (
          <Tooltip title="Export the filtered list to Excel (all pages)">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={onExport}
                disabled={loading || exporting}
                aria-label="Export workflow list to Excel"
              >
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Refresh Button */}
        <Tooltip title="Refresh data">
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh data"
              size="small"
            >
              <RefreshIcon
                sx={{
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }
);

WorkflowToolbar.displayName = 'WorkflowToolbar';

export default WorkflowToolbar;
